import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty, isNil } from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GenerationJobsService } from 'api';
import { companiesAction, firmsAction, globalAction } from 'common/actions';
import { ERROR_403 } from 'common/config/api';
import { forbiddenPath, notFoundPath } from 'common/config/urls';
import { COMP_GROUPS_SLUG } from 'common/constants/firms';
import { useStore } from 'common/store';
import type { ChildCompany, CompanyInfo, FirmCompany } from 'common/types/company';
import { Firm, FIRM_LIST_STATUSES } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import {
  GuidedTourProvider,
  LoadingState,
  TertiaryNavigationSkeleton,
} from 'components/ui';
import { useGlobalKeyboardShortcut } from 'components/ui/hooks';
import { EventType, useEventBus } from 'context/EventBusContext';
import { FirmBaseLayoutContextProvider } from 'context/FirmBaseLayoutContext';
import { NotificationCenterProvider } from 'context/NotificationCenterContext';
import { useZoom } from 'context/ZoomContext';
import { PRIMARY_NAV_SLUGS } from 'layouts/FirmBaseLayout/PrimaryNavigation/PrimaryNavigation.constants';
import { FirmStepper } from 'layouts/Main/components';
import { FirmService } from 'services';
import { useGetFundListByFirmId } from 'services/hooks/firm';
import { useGetCompanyListByFirm } from 'services/hooks/firms';
import { useGetLowerUserPermissions } from 'services/hooks/users';
import { runUserPreferenceMigrations } from 'services/storage/preferencesIndexedDB';
import {
  ContentArea,
  NavigationTourTarget,
  PageContent,
  Root,
  ShellChrome,
  WorkspaceSurface,
} from './FirmBaseLayout.styles';
import FirmBaseLayoutLegacyBridge from './FirmBaseLayoutLegacyBridge';
import PrimaryNavigation from './PrimaryNavigation';
import SecondaryNavigation, { FirmBaseSecondaryNavigationSkeleton } from './SecondaryNavigation';
import SuperSearch, { type SuperSearchContext } from './SuperSearch';
import TertiaryNavigation from './TertiaryNavigation';
import { FirmBaseLayoutProductTours, GUIDED_TOUR_TARGETS } from './tours';

interface FirmBaseLayoutProps {
  children: ReactNode;
}

// Add slugs here only for workspace pages that still need the legacy 80% visual baseline.
// Remove each slug after that page is migrated to shared zoom-aware primitives.
const WORKSPACE_ZOOM_COMPENSATED_PRIMARY_SLUGS = new Set<string>([PRIMARY_NAV_SLUGS.DOCUMENTS]);

// Pane-based workspaces own their internal scroll. Legacy pages should keep PageContent as the scroll owner.
const FULL_HEIGHT_WORKSPACE_PRIMARY_SLUGS = new Set<string>([PRIMARY_NAV_SLUGS.DOCUMENTS]);

const HOME_SLUG = 'home';

const getPrimaryRouteSlug = (firmSlugParam: string | undefined, pathname: string) => {
  if (!firmSlugParam) {
    return undefined;
  }

  return pathname.replace(`/firms/${firmSlugParam}`, '').split('/').filter(Boolean)[0];
};

const FirmBaseLayout = ({ children }: FirmBaseLayoutProps) => {
  const { firmSlugParam, companySlugParam } = useParams<{ firmSlugParam: string; companySlugParam: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { zoomLevel } = useZoom();
  const [
    {
      firmList,
      firmListStatus = FIRM_LIST_STATUSES.IDLE,
      firmId,
      companyList,
      companyInfo,
      otherPermissions,
      user,
      isFirmStepperVisible,
    },
    dispatch,
  ] = useStore() as UseStoreValues;
  const { getFunds } = useGetFundListByFirmId();
  const [, getLowerUserPermissions] = useGetLowerUserPermissions();
  const { publish } = useEventBus();
  const [isSuperSearchOpen, setIsSuperSearchOpen] = useState(false);
  const [superSearchAuthorizationRevision, setSuperSearchAuthorizationRevision] =
    useState<string | null>(null);
  const [hasRefreshedFirmAccess, setHasRefreshedFirmAccess] = useState(false);
  const primaryRouteSlug = getPrimaryRouteSlug(firmSlugParam, location.pathname);
  // Global Comp Groups uses firm slug 0 as a superadmin-only sentinel, not a firm record.
  const isGlobalCompGroupsPath = firmSlugParam === '0' && primaryRouteSlug === COMP_GROUPS_SLUG;
  const isGlobalCompGroupsRoute = Boolean(isGlobalCompGroupsPath && user?.is_superuser);
  const {
    data: firmCompanies,
    isLoading: isLoadingFirmCompanies,
  } = useGetCompanyListByFirm({
    firmId,
    shouldQueryAutomatically: Boolean(firmId) && !isGlobalCompGroupsPath,
  });

  // Refresh firm access and field attributes for this workspace mount. Ignore any
  // response that finishes after logout or navigation unmounts this session shell.
  useEffect(() => {
    let isCurrentRequest = true;

    const fetchFirmList = async () => {
      const response = await firmsAction.getFirmList();

      if (!isCurrentRequest) {
        return;
      }

      const { authorizationRevision, firms, field_attributes: fieldAttributes } = response ?? {};

      if (firms !== ERROR_403) {
        if (!Array.isArray(firms)) {
          setSuperSearchAuthorizationRevision(null);
          return;
        }

        dispatch(firmsAction.setFirmList(firms));
        dispatch(firmsAction.setFirmListStatus(FIRM_LIST_STATUSES.READY));
        setSuperSearchAuthorizationRevision(
          typeof authorizationRevision === 'string' ? authorizationRevision : null
        );
      } else {
        dispatch(firmsAction.setFirmList(null));
        dispatch(firmsAction.setFirmListStatus(FIRM_LIST_STATUSES.FORBIDDEN));
        setSuperSearchAuthorizationRevision(null);
      }
      dispatch(globalAction.setFieldAttrs(fieldAttributes));
      setHasRefreshedFirmAccess(true);
    };

    fetchFirmList();

    return () => {
      isCurrentRequest = false;
    };
  }, [dispatch]);

  // Keep lower-level permissions available for Prism actions without blocking shell hydration.
  useEffect(() => {
    if (user?.id && isNil(otherPermissions)) {
      getLowerUserPermissions(user.id);
    }
  }, [user, otherPermissions, getLowerUserPermissions]);

  // Run legacy pinned/sidebar preference migration from the active Prism shell.
  useEffect(() => {
    if (user?.id) {
      runUserPreferenceMigrations(user.id);
    }
  }, [user?.id]);

  const activeFirm = useMemo(() => {
    if (isGlobalCompGroupsPath) {
      return null;
    }

    if (firmListStatus !== FIRM_LIST_STATUSES.READY) {
      return null;
    }

    const firmListResponse = firmList as Firm[] | null | typeof ERROR_403;
    if (!Array.isArray(firmListResponse) || isEmpty(firmListResponse) || !firmSlugParam) {
      return null;
    }
    return firmListResponse.find(f => f.slug === firmSlugParam) ?? null;
  }, [firmList, firmListStatus, firmSlugParam, isGlobalCompGroupsPath]);

  const shouldRedirectToForbidden = useMemo(() => {
    if (isGlobalCompGroupsPath) {
      return user !== null && !user?.is_superuser;
    }

    // Store state may still describe the previous session for one render. Only deny access
    // after this layout confirms the firm list for the current authenticated session.
    if (!hasRefreshedFirmAccess) {
      return false;
    }

    if (firmListStatus === FIRM_LIST_STATUSES.FORBIDDEN) {
      return true;
    }

    if (firmListStatus !== FIRM_LIST_STATUSES.READY) {
      return false;
    }

    const firmListResponse = firmList as Firm[] | null | typeof ERROR_403;
    return Boolean(Array.isArray(firmListResponse) && firmSlugParam && !activeFirm);
  }, [activeFirm, firmList, firmListStatus, firmSlugParam, hasRefreshedFirmAccess, isGlobalCompGroupsPath, user]);

  // Redirect only after access validation and replace the invalid route in browser history.
  useEffect(() => {
    if (shouldRedirectToForbidden) {
      navigate(forbiddenPath, { replace: true });
    }
  }, [navigate, shouldRedirectToForbidden]);

  // Bridge the active firm from the URL into the legacy store consumed by workspace pages.
  useEffect(() => {
    if (activeFirm) {
      dispatch(firmsAction.setFirmId(activeFirm.id));
      dispatch(firmsAction.setFirmInfo(activeFirm));
    }
  }, [activeFirm, dispatch]);

  // Refresh full firm metadata and funds once the active firm id is available.
  useEffect(() => {
    if (firmId && !isGlobalCompGroupsPath) {
      (async () => {
        const response = (await firmsAction.getFirmById(firmId)) as { firmInfo: Firm };
        if (response?.firmInfo) {
          dispatch(firmsAction.setFirmInfo(response.firmInfo));
        }
      })();
      getFunds(firmId);
    }
  }, [dispatch, firmId, getFunds, isGlobalCompGroupsPath]);

  // Bridge query-backed company lists into the legacy store used by pages and navigation chips.
  useEffect(() => {
    if (firmCompanies && !isGlobalCompGroupsPath) {
      dispatch(companiesAction.setCompanyList(firmCompanies));
    }
  }, [dispatch, firmCompanies, isGlobalCompGroupsPath]);

  const allCompanies = useMemo<Array<FirmCompany | ChildCompany>>(
    () => (companyList ?? []).flatMap(company => [company, ...(company.child_companies ?? [])]),
    [companyList]
  );

  const activeCompany = useMemo(
    () => (companySlugParam ? (allCompanies.find(company => company.company_slug === companySlugParam) ?? null) : null),
    [allCompanies, companySlugParam]
  );

  // Bridge the active company from the URL before the extended company info request completes.
  useEffect(() => {
    if (!companySlugParam) {
      dispatch(companiesAction.setCompanyInfo({} as Partial<CompanyInfo>));
      dispatch(firmsAction.setCompanyMeasurementDateList(null));
      return undefined;
    }
    if (!firmId || !activeCompany) {
      return undefined;
    }

    const activeCompanyId = Number(activeCompany.company_id);

    if (!Number.isFinite(activeCompanyId)) {
      return undefined;
    }
    const activeCompanyVintageYear =
      'company_vintage_year' in activeCompany ? activeCompany.company_vintage_year : activeCompany.vintage_year;

    // Dispatch id/name/slug synchronously so downstream query keys and guards
    // reflect the new company immediately, without waiting for the extended-info fetch.
    // Clear measurement dates from the previous company so consumers don't pair the new
    // company id with a stale measurement date id before the new list loads.
    dispatch(
      companiesAction.setCompanyInfo({
        id: activeCompanyId,
        name: activeCompany.company_name,
        slug: activeCompany.company_slug,
        vintage_year: activeCompanyVintageYear,
        measurement_date_slug: activeCompany.measurement_date_slug,
      } as Partial<CompanyInfo>)
    );
    dispatch(firmsAction.setCompanyMeasurementDateList(null));

    let isStale = false;

    (async () => {
      const firmService = new FirmService();
      const extendedInfo = await firmService.getFirmCompanyExtendedInfo(firmId, activeCompanyId);
      if (isStale) {
        return;
      }

      dispatch(
        companiesAction.setCompanyInfo({
          id: activeCompanyId,
          name: activeCompany.company_name,
          slug: activeCompany.company_slug,
          vintage_year: activeCompanyVintageYear,
          ...extendedInfo,
        } as Partial<CompanyInfo>)
      );
    })();

    return () => {
      isStale = true;
    };
  }, [companySlugParam, activeCompany, firmId, dispatch]);

  const handleJobCompleted = useCallback(
    async (job: GenerationJobPayload) => {
      if (job.processingType === 'FINANCIAL_MAPPING') {
        try {
          await GenerationJobsService.apiGenerationJobsAttributeMappingsCreate(job.jobId);
          publish(EventType.FIN_MAP_ATT_MAPPER_UPDATED_VIA_GEN_JOB_COMPLETED, job.jobId);
        } catch {
          // Mapping creation failed but the job itself completed; silently log.
        }
      }
    },
    [publish]
  );

  // Route/store mismatches mean the nav still has previous workspace data; reserve the shell until it catches up.
  const isFirmListLoading = firmListStatus === FIRM_LIST_STATUSES.IDLE && !Array.isArray(firmList);
  const isFirmRouteHydrating = isGlobalCompGroupsPath
    ? user === null
    : Boolean(firmSlugParam) && (!activeFirm || firmId !== activeFirm.id);
  const isPrimaryNavigationLoading = isFirmListLoading || isFirmRouteHydrating;
  const isCompanyListSyncing = Boolean(firmCompanies) && companyList !== firmCompanies;
  // isFetchingFirmCompanies is intentionally excluded: background refetches
  // should not disable the chip when cached data is already available.
  const isCompanyListLoading = isPrimaryNavigationLoading
    || (Boolean(firmId) && (companyList === null || isLoadingFirmCompanies))
    || isCompanyListSyncing;
  const isCompanyRouteHydrating = Boolean(companySlugParam)
    && (
      isCompanyListLoading
      || !activeCompany
      || (
        String(companyInfo?.id ?? '') !== String(activeCompany.company_id)
        || companyInfo?.slug !== activeCompany.company_slug
      )
    );
  const shouldHoldWorkspaceRoute = isCompanyRouteHydrating;
  const layoutRegistrationKey = `${firmSlugParam ?? ''}:${companySlugParam ?? ''}`;
  const shouldCompensateWorkspaceZoom = primaryRouteSlug
    ? WORKSPACE_ZOOM_COMPENSATED_PRIMARY_SLUGS.has(primaryRouteSlug)
    : false;
  const shouldConstrainWorkspaceHeight = primaryRouteSlug
    ? FULL_HEIGHT_WORKSPACE_PRIMARY_SLUGS.has(primaryRouteSlug)
    : false;
  const superSearchContext = useMemo<SuperSearchContext>(() => {
    if (!activeFirm) {
      return {};
    }

    const context: SuperSearchContext = {
      firm: {
        id: activeFirm.id,
        name: activeFirm.name,
        slug: activeFirm.slug,
      },
    };
    const activeCompanyId = Number(activeCompany?.company_id);

    if (activeCompany && Number.isFinite(activeCompanyId)) {
      context.company = {
        firm_id: activeFirm.id,
        id: activeCompanyId,
        name: activeCompany.company_name,
        slug: activeCompany.company_slug,
      };
    }

    return context;
  }, [activeCompany, activeFirm]);
  const handleSuperSearchOpen = useCallback(() => setIsSuperSearchOpen(true), []);
  const handleSuperSearchClose = useCallback(() => setIsSuperSearchOpen(false), []);
  const handleSuperSearchShortcut = useCallback((event: KeyboardEvent) => {
    event.preventDefault();
    setIsSuperSearchOpen(true);
  }, []);

  useGlobalKeyboardShortcut({
    enabled: !isPrimaryNavigationLoading && !shouldHoldWorkspaceRoute,
    key: 'k',
    metaOrCtrlKey: true,
    onKeyDown: handleSuperSearchShortcut,
  });

  // Once companies finish loading, invalid company URLs should navigate away instead of showing a permanent skeleton.
  useEffect(() => {
    if (companySlugParam && !isCompanyListLoading && !activeCompany) {
      navigate(notFoundPath);
    }
  }, [activeCompany, companySlugParam, isCompanyListLoading, navigate]);

  const layout = (
    <FirmBaseLayoutContextProvider key={layoutRegistrationKey}>
      <GuidedTourProvider>
        <FirmBaseLayoutProductTours />
        <FirmBaseLayoutLegacyBridge>
          <Root>
            <ShellChrome>
              <PrimaryNavigation
                data-tour={GUIDED_TOUR_TARGETS.primaryNavigation}
                isGlobalCompGroupsRoute={isGlobalCompGroupsRoute}
                isCompanyListLoading={isCompanyListLoading}
                isNavigationLoading={isPrimaryNavigationLoading || shouldHoldWorkspaceRoute}
                onSuperSearchOpen={handleSuperSearchOpen}
              />
              {primaryRouteSlug !== HOME_SLUG && (
                shouldHoldWorkspaceRoute ? (
                  <>
                    <FirmBaseSecondaryNavigationSkeleton
                      ariaLabel="Loading company navigation"
                      showActions={false}
                      showStatus={false}
                      zoomLevel={zoomLevel}
                    />
                    <TertiaryNavigationSkeleton
                      ariaLabel="Loading company actions"
                      showActions={false}
                      showLeftAction={false}
                      zoomLevel={zoomLevel}
                    />
                  </>
                ) : (
                  <>
                    <SecondaryNavigation data-tour={GUIDED_TOUR_TARGETS.secondaryNavigation} />
                    <NavigationTourTarget data-tour={GUIDED_TOUR_TARGETS.tertiaryNavigation}>
                      <TertiaryNavigation />
                    </NavigationTourTarget>
                  </>
                )
              )}
            </ShellChrome>
            <ContentArea>
              <PageContent>
                <WorkspaceSurface
                  shouldCompensateZoom={shouldCompensateWorkspaceZoom}
                  shouldConstrainHeight={shouldConstrainWorkspaceHeight}>
                  {shouldHoldWorkspaceRoute ? <LoadingState minHeight="calc(100vh - 160px)" /> : children}
                </WorkspaceSurface>
              </PageContent>
            </ContentArea>
            {isFirmStepperVisible && (
              <FirmStepper
                isVisible={isFirmStepperVisible}
                onClose={() => dispatch(globalAction.showFirmStepper(false))}
              />
            )}
            <SuperSearch
              authorizationRevision={superSearchAuthorizationRevision}
              initialContext={superSearchContext}
              onClose={handleSuperSearchClose}
              open={isSuperSearchOpen}
              zoomLevel={zoomLevel}
            />
          </Root>
        </FirmBaseLayoutLegacyBridge>
      </GuidedTourProvider>
    </FirmBaseLayoutContextProvider>
  );

  return (
    <NotificationCenterProvider
      channelName={user?.id ? `user:${user.id}` : ''}
      companySlug={companySlugParam}
      enableGlobalChannel={Boolean(user?.id)}
      onJobCompleted={handleJobCompleted}>
      {layout}
    </NotificationCenterProvider>
  );
};

export default FirmBaseLayout;
